import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate, fmtCurrency } from "@/lib/api";
import { ProgressTracker, StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, MapPin, Users as UsersIcon, Calendar as CalIcon, Search, FileText, Receipt, Download, BadgeCheck } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import SocialLinks from "@/components/SocialLinks";
import PingMap from "@/components/PingMap";
import { toast } from "sonner";

export default function PublicTrackingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [input, setInput] = useState(code || "");
  const [loading, setLoading] = useState(false);

  const load = async (c) => {
    setLoading(true);
    try {
      const r = await api.get(`/track/${c.toUpperCase()}`);
      setData(r.data);
    } catch {
      toast.error("Order not found");
      setData(null);
    } finally { setLoading(false); }
  };

  React.useEffect(() => { if (code) load(code); /* eslint-disable-next-line */ }, [code]);

  // Auto-refresh while a driver is sharing live location
  React.useEffect(() => {
    if (!code || !data?.driver_location) return;
    const t = setInterval(() => load(code), 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [code, !!data?.driver_location]);

  const submit = (e) => {
    e.preventDefault();
    if (input) navigate(`/track/${input}`);
  };

  const downloadDoc = async (d) => {
    try {
      const r = await api.get(`/track/${(code || input).toUpperCase()}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || `${d.doc_type}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed, please try again"); }
  };

  return (
    <div className="min-h-screen bg-[#050505] py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <BrandMark size={40} eyebrow="Order Tracking" />
        </div>

        <form onSubmit={submit} className="flex gap-2 mb-8">
          <Input value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="Tracking code (e.g. ABC123XYZ)" data-testid="track-input"
            className="bg-[#0a0a0a] border-[#2a2a2a] h-11 rounded-sm" />
          <Button type="submit" data-testid="track-submit" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm h-11">
            <Search className="w-4 h-4 mr-1.5" /> Track
          </Button>
        </form>

        {loading && <div className="text-center text-zinc-600 py-10">Looking up your order…</div>}

        {data && (
          <div className="space-y-4 fade-up">
            <div className="card-lux p-8">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="label-eyebrow">{data.event_type}</div>
                  <h1 className="font-display text-4xl mt-2">{data.customer_name}</h1>
                  <p className="text-zinc-400 mt-1">{data.package_name}</p>
                </div>
                <StatusBadge status={data.status} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                <Cell icon={CalIcon} label="Event" value={fmtDate(data.event_date)} />
                <Cell icon={UsersIcon} label="Pax" value={data.pax} />
                <Cell icon={MapPin} label="Location" value={data.event_location} />
                <Cell label="Balance" value={fmtCurrency(data.balance)} tint={data.balance > 0 ? "#F59E0B" : "#10B981"} />
              </div>
            </div>

            <div className="card-lux p-8">
              <div className="label-eyebrow">Progress</div>
              <h3 className="font-display text-2xl mt-1 mb-4">Live status</h3>
              <ProgressTracker status={data.status} history={data.status_history} />
            </div>

            {data.driver_location && (
              <div className="card-lux p-8" data-testid="driver-location-card">
                <div className="label-eyebrow">Delivery Team</div>
                <h3 className="font-display text-2xl mt-1 mb-1">Driver location</h3>
                <p className="text-xs text-zinc-500 mb-4 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Last updated {fmtDate(data.driver_location.at)} — refreshes automatically
                </p>
                <PingMap pings={[data.driver_location]} height={280} />
              </div>
            )}

            {data.documents?.length > 0 && (
              <div className="card-lux p-8" data-testid="track-documents-card">
                <div className="label-eyebrow flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Official Documents</div>
                <h3 className="font-display text-2xl mt-1 mb-4">Invoices & receipts</h3>
                <div className="space-y-2">
                  {data.documents.map((d) => (
                    <button key={d.id} onClick={() => downloadDoc(d)} data-testid={`track-doc-${d.id}`}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors text-sm">
                      <span className="flex items-center gap-2 min-w-0">
                        {d.doc_type === "receipt" ? <Receipt className="w-4 h-4 shrink-0" /> : <FileText className="w-4 h-4 shrink-0" />}
                        <span className="truncate">{d.original_filename}</span>
                        <span className="text-xs text-zinc-500 capitalize shrink-0">· {d.doc_type}</span>
                      </span>
                      <Download className="w-4 h-4 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {data.timeline?.length > 0 && (
              <div className="card-lux p-8">
                <div className="label-eyebrow">Event Day Timeline</div>
                <h3 className="font-display text-2xl mt-1 mb-4">Schedule</h3>
                <div className="space-y-2">
                  {data.timeline.map((tl) => (
                    <div key={tl.id} className="flex items-center justify-between text-sm py-2 border-b border-[#1a1a1a] last:border-0">
                      <span className={tl.done ? "line-through text-zinc-600" : "text-zinc-200"}>{tl.label}</span>
                      <span className="text-xs text-zinc-500">{fmtDate(tl.time)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.photos?.length > 0 && (
              <div className="card-lux p-8">
                <div className="label-eyebrow">Gallery</div>
                <h3 className="font-display text-2xl mt-1 mb-4">Photos</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.photos.map((p, i) => (
                    <div key={i} className="aspect-square rounded-sm overflow-hidden border border-[#1a1a1a]">
                      <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <SocialLinks />
      </div>
    </div>
  );
}

function Cell({ icon: Icon, label, value, tint }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-zinc-500">{Icon && <Icon className="w-3 h-3" />}{label}</div>
      <div className="text-sm mt-1" style={{ color: tint || "#FAFAFA" }}>{value}</div>
    </div>
  );
}
